import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiChevronLeft, FiChevronRight, FiSettings, FiHelpCircle, FiSearch, FiPlus, FiMenu, FiChevronDown } from 'react-icons/fi';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/pt-br';
import { usePageTitle } from '../contexts/PageTitleContext';


moment.locale('pt-br');
const localizer = momentLocalizer(moment);

const salesOverviewData = [
  { name: 'Ativos', value: 652, color: '#3f1057' },
  { name: 'Inativos', value: 150, color: '#d6c8dc' },
];

const revenueUpdatesData = [
  { name: 'vencidas', value: 20 },
  { name: 'hoje', value: 45 },
  { name: 'a vencer', value: 60 },
];

const alertsData = [
  { id: 1, title: 'Fatura vencida', client: 'Empresa ABC Ltda' },
  { id: 2, title: 'Atualização pendente', client: 'Supermercados XYZ' },
  { id: 3, title: 'Renovação contrato', client: 'Tech Solutions' },
  { id: 4, title: 'Reunião agendada', client: 'Consultoria Silva' },
];

const DashboardCard = ({ title, children }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
    <h3 className="text-gray-600 text-sm font-medium mb-6">{title}</h3>
    {children}
  </div>
);

const AlertCard = ({ title, alerts }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-full">
    <h3 className="text-gray-600 text-sm font-medium mb-6">{title}</h3>
    <div className="flex flex-col gap-3 flex-grow">
      {alerts.map((alert, index) => (
        <div
          key={alert.id}
          className={`flex items-center p-3 ${
            index !== alerts.length - 1 ? 'border-b border-gray-100' : ''
          }`}
        >
          <div
            className="p-2 rounded-lg mr-3"
            style={{ backgroundColor: '#d6c8dc', color: '#3f1057' }}
          >
            <FiAlertTriangle size={16} />
          </div>
          <div className="flex-grow">
            <p className="text-sm font-medium text-gray-800">{alert.title}</p>
            <p className="text-xs text-gray-500">{alert.client}</p>
          </div>
          <div
            className="text-xs font-medium px-2 py-1 rounded-full"
            style={{ backgroundColor: '#d6c8dc', color: '#3f1057' }}
          >
            Prioridade
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SalesOverviewChart = () => (
  <div className="flex flex-col items-center">
    <ResponsiveContainer width="100%" height={180}>
      <PieChart>
        <Pie
          data={salesOverviewData}
          cx="50%"
          cy="50%"
          innerRadius={65}
          outerRadius={85}
          paddingAngle={6}
          cornerRadius={10}
          dataKey="value"
          stroke="none"
          labelLine={false}
          startAngle={90}
          endAngle={450}
          label={({ cx, cy }) => (
            <text
              x={cx}
              y={cy}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-gray-800 text-xl font-bold"
            >
              652
            </text>
          )}
        >
          {salesOverviewData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>

    <div className="mt-6 flex justify-center gap-10 text-sm text-gray-700">
      {salesOverviewData.map((entry, index) => (
        <div key={index} className="flex flex-col items-center">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span>{entry.name}</span>
          </div>
          <span className="mt-1 font-semibold text-gray-900">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const RevenueUpdatesChart = () => {
  const legendData = [
    { name: 'Vencidas', color: '#d6c8dc' },
    { name: 'Hoje', color: '#3f1057' },
    { name: 'A vencer', color: '#d6c8dc' },
  ];
    const { setTitle } = usePageTitle();
  
    useEffect(() => {
      setTitle('Home');
    }, [setTitle]);

  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width="100%" height={180}>
        <BarChart
          data={revenueUpdatesData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          barCategoryGap="20%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#9CA3AF' }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#9CA3AF' }}
            domain={[0, 70]}
          />
          <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={30}>
            {revenueUpdatesData.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={index === 1 ? '#3f1057' : '#d6c8dc'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-4 flex justify-center gap-8 text-sm text-gray-700">
        {legendData.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="font-semibold text-gray-900">{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const OriginalDashboard = () => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
    <DashboardCard title="Base de Clientes">
      <SalesOverviewChart />
    </DashboardCard>
    <DashboardCard title="Tarefas Pendentes">
      <RevenueUpdatesChart />
    </DashboardCard>
    <AlertCard title="Prioridades" alerts={alertsData} />
  </div>
);

const CalendarSidebar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 5)); // Junho de 2025
  
  const getDaysArray = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    const days = [];
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = 1; i <= firstDayOfMonth; i++) {
      days.push({
        day: prevMonthDays - firstDayOfMonth + i,
        currentMonth: false,
        date: new Date(year, month - 1, prevMonthDays - firstDayOfMonth + i),
      });
    }
    
    // Dias do mês atual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        currentMonth: true,
        date: new Date(year, month, i),
        isToday: new Date(year, month, i).toDateString() === new Date(2025, 5, 26).toDateString(),
      });
    }
    
    // Dias do próximo mês para completar a grade
    const remainingDays = 7 - (days.length % 7 || 7);
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        currentMonth: false,
        date: new Date(year, month + 1, i),
      });
    }
    
    return days;
  };
  
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };
  
  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  
  const days = getDaysArray();
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  const weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  return (
    <div className="w-64 pr-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm font-medium text-gray-700">
            {`${monthNames[currentMonth.getMonth()]} de ${currentMonth.getFullYear()}`}
          </div>
          <div className="flex">
            <button onClick={handlePrevMonth} className="p-1 text-gray-600 hover:bg-gray-100 rounded-full">
              <FiChevronLeft size={16} />
            </button>
            <button onClick={handleNextMonth} className="p-1 text-gray-600 hover:bg-gray-100 rounded-full">
              <FiChevronRight size={16} />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-7 mb-2">
          {weekDays.map((day, index) => (
            <div key={index} className="text-center text-xs text-gray-500 font-medium">
              {day}
            </div>
          ))}
        </div>
        
        {/* Dias do mês */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => (
            <div 
              key={index} 
              className={`text-center text-xs p-1 ${
                day.currentMonth ? 'text-gray-800' : 'text-gray-400'
              } ${
                day.isToday ? 
                'bg-[#3f1057] text-white rounded-full' : 
                'hover:bg-gray-100 rounded-full cursor-pointer'
              }`}
            >
              {day.day}
            </div>
          ))}
        </div>
        
        <div className="mt-4">
          <button className="flex items-center w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-full hover:shadow-sm text-gray-700 font-medium">
            <FiPlus size={18} className="mr-2" />
            Criar
            <FiChevronRight size={16} className="ml-auto" />
          </button>
        </div>
      </div>
      
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-700">Minhas agendas</div>
          <button className="text-gray-500 hover:bg-gray-100 rounded-full p-1">
            <FiChevronDown size={16} />
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center">
            <input type="checkbox" className="w-4 h-4 rounded text-[#3f1057] mr-2" defaultChecked />
            <span className="text-sm text-gray-700">Marcelly Odos</span>
          </div>
          <div className="flex items-center">
            <input type="checkbox" className="w-4 h-4 rounded text-green-500 mr-2" defaultChecked />
            <span className="text-sm text-gray-700">Aniversários</span>
          </div>
          <div className="flex items-center">
            <input type="checkbox" className="w-4 h-4 rounded text-indigo-500 mr-2" defaultChecked />
            <span className="text-sm text-gray-700">Tarefas</span>
          </div>
        </div>
      </div>
      
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-700">Outras agendas</div>
          <div className="flex">
            <button className="text-gray-500 hover:bg-gray-100 rounded-full p-1">
              <FiPlus size={16} />
            </button>
            <button className="text-gray-500 hover:bg-gray-100 rounded-full p-1">
              <FiChevronDown size={16} />
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center">
            <input type="checkbox" className="w-4 h-4 rounded text-red-500 mr-2" defaultChecked />
            <span className="text-sm text-gray-700">Feriados no Brasil</span>
          </div>
        </div>
      </div>
      
      <div className="bg-blue-50 rounded-lg p-3">
        <div className="flex items-center">
          <FiSearch size={16} className="text-gray-500 mr-2" />
          <span className="text-sm text-gray-600">Pesquisar pessoas</span>
        </div>
      </div>
    </div>
  );
};

const GoogleStyleCalendar = () => {
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date(2025, 5, 26));
  const [events, setEvents] = useState([
    {
      title: 'Reunião com cliente ABC',
      start: new Date(2025, 5, 23, 9, 0),
      end: new Date(2025, 5, 23, 10, 30),
      color: '#4285F4',
    },
    {
      title: 'Revisão de projeto',
      start: new Date(2025, 5, 23, 13, 0),
      end: new Date(2025, 5, 23, 14, 0),
      color: '#0B8043',
    },
    {
      title: 'Apresentação para equipe',
      start: new Date(2025, 5, 26, 10, 0),
      end: new Date(2025, 5, 26, 11, 30),
      color: '#D50000',
    },
    {
      title: 'Reunião com fornecedor',
      start: new Date(2025, 5, 26, 14, 0),
      end: new Date(2025, 5, 26, 15, 0),
      color: '#F4511E',
    },
  ]);

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  
  const handleNextPeriod = () => {
    const newDate = new Date(date);
    if (view === 'month') {
      newDate.setMonth(date.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(date.getDate() + 7);
    } else if (view === 'day') {
      newDate.setDate(date.getDate() + 1);
    }
    setDate(newDate);
  };

  const handlePrevPeriod = () => {
    const newDate = new Date(date);
    if (view === 'month') {
      newDate.setMonth(date.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(date.getDate() - 7);
    } else if (view === 'day') {
      newDate.setDate(date.getDate() - 1);
    }
    setDate(newDate);
  };

  const handleToday = () => {
    setDate(new Date(2025, 5, 26)); 
  };

  const handleSelectEvent = (event) => {
    const newTitle = prompt('Editar título do evento:', event.title);
    if (newTitle) {
      const updatedEvents = events.map((e) =>
        e === event ? { ...e, title: newTitle } : e
      );
      setEvents(updatedEvents);
    }
  };

  const handleSelectSlot = ({ start, end }) => {
    const newTitle = prompt('Título do novo evento:');
    if (newTitle) {
      const colors = ['#4285F4', '#0B8043', '#D50000', '#F4511E', '#8E24AA'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      
      const newEvent = {
        title: newTitle,
        start,
        end,
        color: randomColor,
      };
      setEvents([...events, newEvent]);
    }
  };
  
  const eventPropGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color || '#4285F4',
        borderRadius: '4px',
        border: 'none',
        color: 'white',
      },
    };
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <FiMenu size={20} color="#5F6368" />
            </button>
            
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-[#3f1057] text-white rounded-md font-bold mr-2">
                26
              </div>
              <span className="font-medium text-xl text-gray-700">Agenda</span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button 
              onClick={handleToday}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Hoje
            </button>
            
            <button 
              onClick={handlePrevPeriod}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <FiChevronLeft size={20} color="#5F6368" />
              <span className="sr-only">Anterior</span>
            </button>
            
            <button 
              onClick={handleNextPeriod}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <FiChevronRight size={20} color="#5F6368" />
              <span className="sr-only">Próximo</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <select 
                value={date.getMonth()} 
                onChange={(e) => setDate(new Date(date.getFullYear(), parseInt(e.target.value), date.getDate()))}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-700"
              >
                {monthNames.map((month, idx) => (
                  <option key={idx} value={idx}>{month}</option>
                ))}
              </select>
              
              <input 
                type="number" 
                value={date.getDate()}
                min="1"
                max="31"
                onChange={(e) => {
                  const newDay = parseInt(e.target.value);
                  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                  if (newDay >= 1 && newDay <= lastDayOfMonth) {
                    setDate(new Date(date.getFullYear(), date.getMonth(), newDay));
                  }
                }}
                className="border border-gray-300 rounded-md w-14 px-2 py-1 text-sm text-gray-700"
              />
              
              <input 
                type="number" 
                value={date.getFullYear()}
                min="2000"
                max="2100"
                onChange={(e) => setDate(new Date(parseInt(e.target.value), date.getMonth(), date.getDate()))}
                className="border border-gray-300 rounded-md w-20 px-2 py-1 text-sm text-gray-700"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="flex border border-gray-300 rounded-md overflow-hidden">
              <button 
                onClick={() => setView('day')}
                className={`px-3 py-1.5 text-sm ${view === 'day' ? 'bg-[#d6c8dc] text-[#3f1057]' : 'bg-white text-gray-700'}`}
              >
                Dia
              </button>
              <button 
                onClick={() => setView('week')}
                className={`px-3 py-1.5 text-sm ${view === 'week' ? 'bg-[#d6c8dc] text-[#3f1057]' : 'bg-white text-gray-700'}`}
              >
                Semana
              </button>
              <button 
                onClick={() => setView('month')}
                className={`px-3 py-1.5 text-sm ${view === 'month' ? 'bg-[#d6c8dc] text-[#3f1057]' : 'bg-white text-gray-700'}`}
              >
                Mês
              </button>
            </div>
            
            <div className="flex items-center space-x-2">
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <FiSearch size={18} color="#5F6368" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <FiSettings size={18} color="#5F6368" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <FiHelpCircle size={18} color="#5F6368" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="calendar-container" style={{ height: '75vh' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable
          eventPropGetter={eventPropGetter}
          popup
          components={{
            toolbar: () => null, 
          }}
          messages={{
            today: 'Hoje',
            previous: 'Anterior',
            next: 'Próximo',
            month: 'Mês',
            week: 'Semana',
            day: 'Dia',
            agenda: 'Agenda',
            date: 'Data',
            time: 'Hora',
            event: 'Evento',
            allDay: 'O dia todo',
            noEventsInRange: 'Não há eventos nesse intervalo',
            showMore: (total) => `+ Ver mais (${total})`,
          }}
        />
      </div>
      
      <div className="fixed bottom-6 right-6">
        <button 
          className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-100 border border-gray-200"
          onClick={() => handleSelectSlot({ start: new Date(), end: new Date(new Date().getTime() + 60*60*1000) })}
        >
          <FiPlus size={24} color="#5F6368" />
        </button>
      </div>
    </div>
  );
};

export default function Home() {
  return (
    <div>
      <div className="p-6 min-h-screen">
        <OriginalDashboard />
        <div className="flex">
          <CalendarSidebar />
          <div className="flex-1">
            <GoogleStyleCalendar />
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = `
  /* Estilo adicional para o calendário */
  .rbc-calendar {
    font-family: 'Google Sans', 'Roboto', Arial, sans-serif;
  }
  
  .rbc-header {
    font-weight: 500;
    padding: 8px 0;
    color: #5F6368;
    border-bottom: 1px solid #E5E7EB;
    background-color: #F8F9FA;
  }
  
  .rbc-month-view {
    border: none;
    border-radius: 8px;
  }
  
  .rbc-day-bg {
    background-color: white;
  }
  
  .rbc-off-range-bg {
    background-color: #F8F9FA;
  }
  
  .rbc-today {
    background-color: #E8F0FE;
  }
  
  .rbc-event {
    padding: 2px 5px;
    border-radius: 4px !important;
    font-weight: 500;
    font-size: 12px;
  }
  
  .rbc-day-slot .rbc-event {
    border-radius: 4px !important;
    padding: 2px 6px;
  }
  
  .rbc-date-cell {
    font-size: 12px;
    padding: 6px;
    text-align: center;
  }
  
  .rbc-month-row {
    overflow: visible;
  }
  
  .rbc-row-content {
    z-index: 0;
  }
  
  .rbc-date-cell.rbc-now {
    font-weight: 500;
    color: #1A73E8;
  }
  
  /* Destacar o dia atual */
  .rbc-date-cell.rbc-now > a {
    background-color: #1A73E8;
    color: white;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: auto;
  }
  
  /* Estilizando o hover nos slots */
  .rbc-slot-selecting {
    background-color: rgba(26, 115, 232, 0.1);
  }

  /* Dia atual no calendário com círculo azul */
  .rbc-date-cell.rbc-now {
    position: relative;
  }
  
  /* Correção para visualização da semana */
  .rbc-time-view {
    border: none;
    border-radius: 8px;
  }
  
  .rbc-time-header-content {
    border-left: none;
  }
  
  .rbc-time-header {
    border-bottom: 1px solid #E5E7EB;
  }
  
  /* Estilos para o modo de exibição do dia */
  .rbc-time-column {
    background-color: white;
  }
  
  .rbc-timeslot-group {
    border-bottom: 1px solid #F3F4F6;
  }
  
  .rbc-time-content {
    border-top: 1px solid #E5E7EB;
  }
  
  .rbc-time-slot {
    color: #5F6368;
  }
  
  /* Estilos adicionais para a barra lateral */
  .rbc-agenda-view table.rbc-agenda-table {
    border: none;
  }
  
  .rbc-agenda-view table.rbc-agenda-table thead > tr > th {
    font-weight: 500;
    color: #5F6368;
    border-bottom: 1px solid #E5E7EB;
    padding: 8px;
  }
  
  .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
    padding: 8px;
    border-bottom: 1px solid #F3F4F6;
  }
  
  .rbc-agenda-view table.rbc-agenda-table tbody > tr:hover {
    background-color: #F9FAFB;
  }
  
  /* Estilo para o botão Semana, deve ter mais destaque quando selecionado */
  .rbc-toolbar button.rbc-active {
    background-color: #E8F0FE;
    color: #1A73E8;
    box-shadow: none;
  }
  
  /* Ajustes para a visualização mensal no Google Agenda */
  .rbc-month-view .rbc-row-segment {
    padding: 0 1px;
  }
  
  .rbc-month-view .rbc-row-segment .rbc-event {
    margin: 1px 0;
  }
  
  /* Melhoria para o botão de criação de eventos */
  .create-event-button {
    box-shadow: 0 2px 6px rgba(60, 64, 67, 0.15);
    transition: box-shadow 0.2s;
  }
  
  .create-event-button:hover {
    box-shadow: 0 4px 8px rgba(60, 64, 67, 0.2);
  }
`;

// Adicione o estilo ao final do documento
if (typeof document !== 'undefined') {
  document.head.appendChild(document.createElement('style')).innerHTML = styles;
}